import type { Elevator, GameState, PowerName } from '@full-elevator/sim';
import { POWER, POWER_NAMES, powerApplies } from '@full-elevator/sim';
import { el, icon } from './dom.js';
import { setData } from './write.js';

/**
 * The power-ups' controls (ADR-0055): a button in the lintel beside Pause whose
 * ring fills with people home in a row, and once it is full, a pop-up with the
 * four to choose from. The shift waits while the pop-up is open, and while the
 * player picks who takes the stairs (the owner, PLAYTEST_LOG 2026-10-29).
 *
 * Words and buttons only. Whether a power does anything now is the sim's
 * (`powerApplies`), and so is what it does.
 */

export const POWER_WORDS: Readonly<
  Record<PowerName, { title: string; line: string; none: string }>
> = {
  squeeze: {
    title: 'Squeeze in',
    line: `${POWER.squeezeSeats} more seats in the lift, for one stop.`,
    none: 'Already squeezed in.',
  },
  hold: {
    title: 'Hold it',
    line: `The next to leave leaves ${POWER.holdMs / 1000}s later.`,
    none: 'Nothing is about to leave.',
  },
  announce: {
    title: 'Announce',
    line: `Everyone waiting waits ${POWER.announceMs / 1000}s longer.`,
    none: 'Nobody waiting is in a hurry.',
  },
  stairs: {
    title: 'Stairs',
    line: `Someone ${POWER.stairsFloors} floors or less from home walks, for half the points.`,
    none: 'Nobody is close enough to walk.',
  },
};

/** What a power just did, said by the car. */
export const POWER_DONE: Readonly<Record<PowerName, string>> = {
  squeeze: 'Squeezed in: two more seats this stop',
  hold: `Held: ${POWER.holdMs / 1000}s more to catch it`,
  announce: 'Please wait: everyone stays a little longer',
  stairs: 'Took the stairs',
};

/** A power's badge: its icon on its own colour, as the pop-up and the card show it. */
export function powerBadge(name: PowerName): HTMLElement {
  return el('span', { class: 'power-badge', 'data-power': name }, [icon(name)]);
}

export interface PowerActions {
  /** The button, with a charge to spend: open the choice and hold the shift. */
  readonly open: () => void;
  readonly choose: (name: PowerName) => void;
  /** The choice closed with the charge kept. */
  readonly later: () => void;
  /** The choice of who walks given up. */
  readonly cancel: () => void;
}

export interface PowerControl {
  /** In the lintel, beside Pause. */
  readonly button: HTMLButtonElement;
  /** The choice, over the whole screen. */
  readonly popup: HTMLElement;
  /** Under the lintel while the player picks who takes the stairs. */
  readonly hint: HTMLElement;
  readonly isOpen: boolean;
  /** Redraw the meter and the charge; hidden where there are no power-ups. */
  sync(state: GameState, shown: boolean, picking: boolean): void;
  /** Open the choice, each power lit or dimmed by whether it would do anything now. */
  open(state: GameState, car: Elevator): void;
  close(): void;
}

function tile(name: PowerName, choose: (name: PowerName) => void): HTMLButtonElement {
  const words = POWER_WORDS[name];
  const button = el('button', { class: 'power-tile', type: 'button', 'data-power': name }, [
    powerBadge(name),
    el('span', { class: 'power-title', text: words.title }),
    el('span', { class: 'power-line', text: words.line }),
    el('span', { class: 'power-none', text: words.none }),
  ]);
  button.addEventListener('click', () => choose(name));
  return button;
}

export function buildPowerControl(actions: PowerActions): PowerControl {
  const button = el(
    'button',
    { class: 'power-button', type: 'button', 'aria-label': 'Power-up', hidden: true },
    [el('span', { class: 'power-face' }, [icon('power')])],
  );

  const tiles = new Map<PowerName, HTMLButtonElement>();
  for (const name of POWER_NAMES) {
    tiles.set(
      name,
      tile(name, (chosen) => {
        control.close();
        actions.choose(chosen);
      }),
    );
  }
  const later = el('button', { class: 'power-later', type: 'button' }, ['Keep it for later']);
  later.addEventListener('click', () => {
    control.close();
    actions.later();
  });
  const card = el(
    'div',
    { class: 'power-card', role: 'dialog', 'aria-label': 'Choose a power-up' },
    [
      el('div', { class: 'power-burst', 'aria-hidden': 'true' }, [
        el('span', { class: 'power-burst-face' }, [icon('power')]),
      ]),
      el('h2', { class: 'power-heading', text: 'Power-up!' }),
      el('p', { class: 'power-sub', text: 'The shift waits while you choose.' }),
      el('div', { class: 'power-grid' }, [...tiles.values()]),
      later,
    ],
  );
  const popup = el('div', { class: 'power-pop', 'data-open': 'false' }, [card]);
  // A tap beside the card keeps the charge, as "later" does.
  popup.addEventListener('click', (event) => {
    if (event.target !== popup) return;
    control.close();
    actions.later();
  });

  const cancel = el('button', { class: 'power-hint-cancel', type: 'button' }, ['Cancel']);
  cancel.addEventListener('click', () => actions.cancel());
  const hint = el('div', { class: 'power-hint', hidden: true }, [
    powerBadge('stairs'),
    el('span', { text: 'Tap who walks' }),
    cancel,
  ]);

  let ready = false;
  let picking = false;
  let fill = -1;
  button.addEventListener('click', () => {
    if (picking) actions.cancel();
    else if (ready) actions.open();
  });

  const control: PowerControl = {
    button,
    popup,
    hint,
    get isOpen() {
      return popup.dataset.open === 'true';
    },
    sync(state, shown, nowPicking) {
      if (button.hidden === shown) button.hidden = !shown;
      const bar = button.parentElement;
      if (bar) setData(bar, 'powers', String(shown));
      if (hint.hidden === nowPicking) hint.hidden = !nowPicking;
      if (!shown) return;
      const power = state.power;
      ready = power.charges > 0;
      picking = nowPicking;
      setData(button, 'ready', String(ready));
      const share = ready ? 1 : power.meter / POWER.meterFull;
      if (share !== fill) {
        fill = share;
        button.style.setProperty('--fill', String(share));
      }
    },
    open(state, car) {
      for (const [name, option] of tiles) option.disabled = !powerApplies(state, name, car);
      popup.dataset.open = 'true';
      // The first power that would do anything, ready for a thumb or a key.
      [...tiles.values()].find((option) => !option.disabled)?.focus({ preventScroll: true });
    },
    close() {
      popup.dataset.open = 'false';
    },
  };
  return control;
}
